/**
 * The public registration form — the front door of the golden path.
 *
 * Routed OUTSIDE `AppLayout`: no sidebar, no staff navigation, no link into the
 * portal. Section 1.7 makes this the only page an unauthenticated visitor sees,
 * and Plan Section 5 makes `POST /registration-requests` the only endpoint it may
 * call. It never reads a record back — the confirmation below is composed here,
 * from nothing the server sent about the submission.
 *
 * Two rules shape the field list:
 *   §8.4.4  the visitor sees questions, never column names. "What would you like
 *           to help with?", not "areas_of_interest".
 *   §8.40   validation here is for UX only. Every rule enforced below is enforced
 *           again in `createRegistrationRequestSchema`, which is the authority.
 *
 * The API accepts the full personal-details set (address lines, alternate phone,
 * education, and the rest); this form asks for the subset a volunteer can answer
 * in a couple of minutes. Staff fill in the remainder on the profile after
 * approval, where the person is already talking to them.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  EMAIL_MESSAGE,
  isEmailish,
  joinCodes,
  INSTITUTION_OTHER,
  OCCUPATION,
  OTHER_ORGANIZATION_ID,
  VOLUNTEER_TYPE,
  VOLUNTEERING_FREQUENCY,
  nationalTenDigits,
} from '@parinaam-vms/shared';
import type { CreateRegistrationRequestBody, FieldError } from '@parinaam-vms/shared';
import {
  BrandLogo,
  Button,
  Card,
  CheckboxGroup,
  ErrorState,
  LoadingState,
  RadioGroup,
  SelectInput,
  TextArea,
  TextInput,
} from '../../components/common';
import { OrganizationPicker } from '../../components/organizations/OrganizationPicker';
import { INDIA_STATES, OTHER_CITY, citiesForState } from '../../constants/indiaLocations';
import {
  AGE_GROUP_OPTIONS,
  FREQUENCY_OPTIONS,
  OCCUPATION_OPTIONS,
  VOLUNTEER_TYPE_OPTIONS,
  toReferenceOptions,
} from '../../constants/volunteerFields';
import { useReferenceValuesGrouped } from '../../hooks/useReferenceValues';
import { ApiRequestError } from '../../services/api/client';
import { submitRegistration } from '../../services/api/registration-requests';
import styles from './PublicRegistrationPage.module.css';

/** The form's own state. Strings throughout, because that is what inputs hold. */
interface FormState {
  volunteer_type: string;
  organization_id: string;
  /** An INSTITUTION reference code. Only ever set for a student. */
  institution: string;
  /** The college typed in, shown and required only when institution is OTHER. */
  institution_other: string;
  /** The employer typed in, shown and required only for the "Other" partner. */
  organization_other: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  /** The chosen state/UT (hardcoded list). Drives which cities are offered. */
  state: string;
  /** A city from the state's list, or OTHER_CITY, or '' when unchosen. */
  city_choice: string;
  /** The typed city, used only when `city_choice` is OTHER_CITY. */
  city_other: string;
  /** An AGE_GROUP code (a fixed set) — required. Replaces date of birth. */
  age_group: string;
  gender: string;
  /** An OCCUPATION code (a fixed set), or '' for unanswered. Individuals only. */
  occupation: string;
  /** The free-text detail shown and required only when `occupation` is OTHER. */
  occupation_other: string;
  /** A REFERRAL_SOURCE code — how they heard about Parinaam. Asked of everyone. */
  referral_source: string;
  areas_of_interest: string[];
  /** How often they want to help. */
  volunteering_frequency: string;
  /** The free-text detail shown and required only when frequency is OTHER. */
  volunteering_frequency_other: string;
  skills: string;
}

const EMPTY_FORM: FormState = {
  volunteer_type: VOLUNTEER_TYPE.INDIVIDUAL,
  organization_id: '',
  institution: '',
  institution_other: '',
  organization_other: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  state: '',
  city_choice: '',
  city_other: '',
  age_group: '',
  gender: '',
  occupation: '',
  occupation_other: '',
  referral_source: '',
  areas_of_interest: [],
  volunteering_frequency: '',
  volunteering_frequency_other: '',
  skills: '',
};

type FieldErrors = Partial<Record<keyof FormState, string>>;

export function PublicRegistrationPage() {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitted, setSubmitted] = useState(false);

  /**
   * The honeypot's value, and when this form appeared.
   *
   * `useState` with an initialiser rather than a plain `Date.now()` in the
   * component body: the body re-runs on every keystroke, and a timestamp that
   * refreshes with it would always read as "submitted instantly" and get every
   * genuine visitor silently discarded.
   */
  const [renderedAt] = useState(() => Date.now());
  const [honeypot, setHoneypot] = useState('');

  const reference = useReferenceValuesGrouped();

  const mutation = useMutation({
    mutationFn: submitRegistration,
    onSuccess: () => {
      setSubmitted(true);
      setErrors({});
    },
    onError: (error: unknown) => {
      // A 422 carries a field list (§5.10). Anything else is shown whole, above
      // the form, because there is no single field to blame.
      setErrors(toFieldErrors(error));
    },
  });

  const set =
    <K extends keyof FormState>(key: K) =>
    (value: FormState[K]) => {
      setForm((current) => ({ ...current, [key]: value }));
      // Clear the field's error as soon as it is touched: leaving a red message
      // under a box the visitor is actively fixing reads as a second failure.
      setErrors((current) => (key in current ? { ...current, [key]: undefined } : current));
    };

  const setVolunteerType = (value: string) => {
    setForm((current) => ({
      ...current,
      volunteer_type: value,
      // Leaving "corporate" drops any organisation already chosen: an individual
      // or a student must not carry one, and the API refuses it if they do.
      organization_id: value === VOLUNTEER_TYPE.CORPORATE ? current.organization_id : '',
      // The same rule for a student's institution, in the other direction. A
      // value left behind after switching away would be refused by the API and
      // by the CHECK constraint, and the person would not know why.
      institution: value === VOLUNTEER_TYPE.STUDENT ? current.institution : '',
      institution_other: value === VOLUNTEER_TYPE.STUDENT ? current.institution_other : '',
      organization_other: value === VOLUNTEER_TYPE.CORPORATE ? current.organization_other : '',
      // Occupation is asked of individuals only — a corporate or student who had
      // answered it before switching would leave a stray value behind.
      occupation: value === VOLUNTEER_TYPE.INDIVIDUAL ? current.occupation : '',
      occupation_other: value === VOLUNTEER_TYPE.INDIVIDUAL ? current.occupation_other : '',
    }));
    setErrors((current) => ({
      ...current,
      volunteer_type: undefined,
      organization_id: undefined,
      institution: undefined,
      institution_other: undefined,
      organization_other: undefined,
      occupation: undefined,
      occupation_other: undefined,
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const problems = validate(form);
    if (Object.keys(problems).length > 0) {
      setErrors(problems);
      return;
    }
    mutation.mutate({
      ...toRequestBody(form),
      website: honeypot,
      form_rendered_at: renderedAt,
    });
  };

  if (reference.isPending) {
    return (
      <Shell>
        <Card>
          <LoadingState label="Loading the form…" />
        </Card>
      </Shell>
    );
  }

  if (reference.isError) {
    return (
      <Shell>
        <Card>
          <ErrorState error={reference.error} onRetry={() => void reference.refetch()} />
        </Card>
      </Shell>
    );
  }

  if (submitted) {
    return (
      <Shell>
        <Card>
          <div className={styles.confirmation} role="status">
            <h2 className={styles.confirmationTitle}>Thank you for registering</h2>
            <p>
              Our team will be in touch. Thank you.
            </p>
            <Button
              variant="secondary"
              onClick={() => {
                setForm(EMPTY_FORM);
                setSubmitted(false);
                mutation.reset();
              }}
            >
              Register someone else
            </Button>
          </div>
        </Card>
      </Shell>
    );
  }

  const options = toReferenceOptions(reference.data);
  const generalError = generalMessage(mutation.error, errors);

  const isCorporate = form.volunteer_type === VOLUNTEER_TYPE.CORPORATE;
  const isStudent = form.volunteer_type === VOLUNTEER_TYPE.STUDENT;
  const isIndividual = form.volunteer_type === VOLUNTEER_TYPE.INDIVIDUAL;

  /**
   * The list loaded, successfully, and is empty.
   *
   * Distinct from `isError`, and worth its own branch because it fails silently
   * where an error does not: the field is mandatory for a student, so an empty
   * dropdown is not a cosmetic gap — it is a form that cannot be submitted, with
   * nothing on screen saying why. Seen locally the first time the pick-list was
   * seeded after a tab had already cached the old one, and reachable in
   * production any time the INSTITUTION category is missing.
   */
  const institutionsMissing = !reference.isPending && options.institutions.length === 0;

  return (
    <Shell>
      <Card>
        <form onSubmit={handleSubmit} noValidate>
          {/*
            The honeypot. Positioned off-screen rather than `display: none`,
            because the cruder bots skip hidden inputs but follow the DOM.

            `aria-hidden` and `tabIndex={-1}` keep it away from screen readers
            and from tab order, so nobody using assistive technology or a
            keyboard can land in it by accident and have their registration
            silently thrown away. `autoComplete="off"` stops a browser helpfully
            filling it from a saved profile, which would do the same.
          */}
          <div className={styles.honeypot} aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              id="website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(event) => setHoneypot(event.target.value)}
            />
          </div>

          {generalError && (
            <p className={styles.formError} role="alert">
              {generalError}
            </p>
          )}

          <fieldset className={styles.section} disabled={mutation.isPending}>
            <legend className={styles.sectionTitle}>How are you volunteering?</legend>

            <RadioGroup
              label="Are you volunteering on your own, through your organisation, or as a student?"
              required
              // Sentences, not terms — "Through my organisation (corporate)" is
              // wider than any sensible grid track, so they go down the page.
              layout="stack"
              options={VOLUNTEER_TYPE_OPTIONS}
              value={form.volunteer_type}
              error={errors.volunteer_type}
              onChange={setVolunteerType}
            />

            {/*
              Shown only for a corporate volunteer, and it is a pick-list, never a
              free-text box: the partner list is maintained by staff, and letting
              a volunteer type their employer's name is how the same company ends
              up recorded three ways. Someone whose employer is not listed simply
              is not corporate here — staff add the partner, then link them.
            */}
            {isCorporate && (
              <>
                <OrganizationPicker
                  label="Which organisation are you volunteering through?"
                  placeholder="Choose your organisation"
                  required
                  value={form.organization_id}
                  error={errors.organization_id}
                  onChange={(value) => {
                    set('organization_id')(value);
                    // Stepping off Other clears the box it revealed, so a stale
                    // employer never travels under a real partner.
                    if (value !== OTHER_ORGANIZATION_ID && form.organization_other !== '') {
                      set('organization_other')('');
                    }
                  }}
                />
                {form.organization_id === OTHER_ORGANIZATION_ID && (
                  <TextInput
                    label="Which organisation?"
                    required
                    autoComplete="organization"
                    value={form.organization_other}
                    error={errors.organization_other}
                    onChange={(event) => set('organization_other')(event.target.value)}
                  />
                )}
              </>
            )}

            {/*
              The student's counterpart, and a pick-list for the same reason the
              organization above is one: `Christ`, `christ university` and
              `CHRIST (Deemed)` typed into a text box are three institutions as
              far as any report is concerned.

              Unlike the corporate list, this one ends in "Other — not listed".
              A student whose college is missing cannot simply pick a different
              type — they *are* a student, and the field is mandatory for them —
              so without that option the form would be a dead end. Staff add the
              real institution afterwards.
            */}
            {isStudent && (
              <SelectInput
                label="Where do you study?"
                required
                placeholder={
                  reference.isPending ? 'Loading institutions…' : 'Choose your institution'
                }
                options={options.institutions}
                value={form.institution}
                error={
                  errors.institution ??
                  (reference.isError
                    ? 'The institution list did not load. Please try again in a moment.'
                    : institutionsMissing
                      ? 'No institutions listed — choose "As an individual".'
                      : undefined)
                }
                onChange={(event) => {
                  const value = event.target.value;
                  set('institution')(value);
                  if (value !== INSTITUTION_OTHER && form.institution_other !== '') {
                    set('institution_other')('');
                  }
                }}
              />
            )}

            {/*
              The college behind "Other — not listed".

              The option has always been there and the answer always went
              nowhere: the form asked where they study, the visitor said, and
              only the code OTHER was kept. Parinaam asked for the box on
              13 Oct 2026.
            */}
            {isStudent && form.institution === INSTITUTION_OTHER && (
              <TextInput
                label="Which college or institution?"
                required
                autoComplete="organization"
                value={form.institution_other}
                error={errors.institution_other}
                onChange={(event) => set('institution_other')(event.target.value)}
              />
            )}
          </fieldset>

          <fieldset className={styles.section} disabled={mutation.isPending}>
            <legend className={styles.sectionTitle}>About you</legend>

            <div className={styles.row}>
              <TextInput
                label="First name"
                required
                autoComplete="given-name"
                value={form.first_name}
                error={errors.first_name}
                onChange={(event) => set('first_name')(event.target.value)}
              />
              <TextInput
                label="Last name"
                required
                autoComplete="family-name"
                value={form.last_name}
                error={errors.last_name}
                onChange={(event) => set('last_name')(event.target.value)}
              />
            </div>

            <div className={styles.row}>
              <TextInput
                label="Email address"
                type="email"
                required
                autoComplete="email"
                value={form.email}
                error={errors.email}
                onChange={(event) => set('email')(event.target.value)}
              />
              <TextInput
                label="Phone number"
                type="tel"
                required
                inputMode="numeric"
                autoComplete="tel"
                maxLength={10}
                value={form.phone}
                error={errors.phone}
                // Ten digits, nothing else: strip anything non-numeric as it is
                // typed and never keep more than ten, so a country code or spaces
                // cannot slip in. The API still checks it besides.
                onChange={(event) =>
                  set('phone')(event.target.value.replace(/\D/g, '').slice(0, 10))
                }
              />
            </div>

            <div className={styles.row}>
              <SelectInput
                label="What age group do you fall under?"
                required
                placeholder="Choose your age group"
                options={AGE_GROUP_OPTIONS}
                value={form.age_group}
                error={errors.age_group}
                onChange={(event) => set('age_group')(event.target.value)}
              />
              <SelectInput
                label="Gender"
                placeholder="Prefer not to answer"
                options={options.gender}
                value={form.gender}
                error={errors.gender}
                onChange={(event) => set('gender')(event.target.value)}
              />
            </div>

            {/*
              State then city, both from a fixed list rather than a free-text box,
              so the master is not littered with "Bangalore"/"Bengaluru"/"bengaluru"
              for one place. The list is not exhaustive, so the city dropdown ends
              in "Other" — which reveals a plain text box for anything not listed.
            */}
            <div className={styles.row}>
              <SelectInput
                label="Which state are you in?"
                required
                placeholder="Choose your state"
                options={INDIA_STATES.map((s) => ({ value: s, label: s }))}
                value={form.state}
                error={errors.state}
                onChange={(event) => {
                  const value = event.target.value;
                  // Cities depend on the state, so changing it clears the city.
                  setForm((current) => ({
                    ...current,
                    state: value,
                    city_choice: '',
                    city_other: '',
                  }));
                  setErrors((current) => ({
                    ...current,
                    state: undefined,
                    city_choice: undefined,
                    city_other: undefined,
                  }));
                }}
              />
              <SelectInput
                label="Current city"
                required
                disabled={form.state === ''}
                placeholder={form.state === '' ? 'Choose a state first' : 'Choose your city'}
                options={[
                  ...citiesForState(form.state).map((c) => ({ value: c, label: c })),
                  { value: OTHER_CITY, label: 'Other (my city is not listed)' },
                ]}
                value={form.city_choice}
                error={errors.city_choice}
                onChange={(event) => {
                  const value = event.target.value;
                  set('city_choice')(value);
                  // Leaving "Other" clears the box it revealed.
                  if (value !== OTHER_CITY && form.city_other !== '') set('city_other')('');
                }}
              />
            </div>
            {form.city_choice === OTHER_CITY && (
              <TextInput
                label="Enter your city"
                required
                autoComplete="address-level2"
                value={form.city_other}
                error={errors.city_other}
                onChange={(event) => set('city_other')(event.target.value)}
              />
            )}

            {/*
              Asked of everybody: the questions above decide what kind of
              volunteer this is, and this one asks how they got here.
              Optional — a blank answer is a real answer, and a required
              field on a public form is a reason to close the tab.

              The placeholder is not "Choose an option": a select whose
              placeholder repeats its label teaches the visitor nothing, and
              this one has to say that skipping it is allowed.
            */}
            <SelectInput
              label="How did you hear about Parinaam?"
              placeholder={reference.isPending ? 'Loading…' : 'Select if you would like to tell us'}
              options={options.referralSources}
              value={form.referral_source}
              error={errors.referral_source}
              onChange={(event) => set('referral_source')(event.target.value)}
            />

            {/*
              Occupation is asked of individuals only — it is a question about the
              person, which a corporate volunteer answers through their employer
              and a student through "As a student". A dropdown rather than a text
              box so the same job is not spelt five ways across the master.
            */}
            {isIndividual && (
              <>
                <SelectInput
                  label="Occupation"
                  options={OCCUPATION_OPTIONS}
                  value={form.occupation}
                  error={errors.occupation}
                  onChange={(event) => {
                    const value = event.target.value;
                    set('occupation')(value);
                    // Stepping off OTHER clears the box it revealed, so a stale
                    // "please specify" answer is never carried under a coded one.
                    if (value !== OCCUPATION.OTHER && form.occupation_other !== '') {
                      set('occupation_other')('');
                    }
                  }}
                />
                {form.occupation === OCCUPATION.OTHER && (
                  <TextInput
                    label="Please specify your occupation"
                    required
                    autoComplete="organization-title"
                    value={form.occupation_other}
                    error={errors.occupation_other}
                    onChange={(event) => set('occupation_other')(event.target.value)}
                  />
                )}
              </>
            )}
          </fieldset>

          <fieldset className={styles.section} disabled={mutation.isPending}>
            <legend className={styles.sectionTitle}>How would you like to help?</legend>

            <CheckboxGroup
              label="What would you like to help with?"
              options={options.areasOfInterest}
              value={form.areas_of_interest}
              error={errors.areas_of_interest}
              onChange={set('areas_of_interest')}
            />

            <RadioGroup
              label="How often would you like to volunteer with Parinaam?"
              options={FREQUENCY_OPTIONS}
              value={form.volunteering_frequency}
              error={errors.volunteering_frequency}
              onChange={(value) => {
                set('volunteering_frequency')(value);
                // Stepping off OTHER clears the box it revealed.
                if (
                  value !== VOLUNTEERING_FREQUENCY.OTHER &&
                  form.volunteering_frequency_other !== ''
                ) {
                  set('volunteering_frequency_other')('');
                }
              }}
            />
            {form.volunteering_frequency === VOLUNTEERING_FREQUENCY.OTHER && (
              <TextInput
                label="Please tell us how often"
                required
                value={form.volunteering_frequency_other}
                error={errors.volunteering_frequency_other}
                onChange={(event) => set('volunteering_frequency_other')(event.target.value)}
              />
            )}

            <TextArea
              label="Anything else you would like us to know?"
              rows={3}
              value={form.skills}
              error={errors.skills}
              onChange={(event) => set('skills')(event.target.value)}
            />
          </fieldset>

          <div className={styles.actions}>
            <Button
              type="submit"
              variant="primary"
              loading={mutation.isPending}
              disabled={mutation.isPending}
            >
              Submit
            </Button>
          </div>
        </form>
      </Card>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Page furniture
// ---------------------------------------------------------------------------

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <header className={styles.header}>
          {/*
            Labelled, not decorative. This page is reached from a link that
            circulates on its own, so the mark is the first thing telling a
            stranger whose form they are filling in — and for a screen reader it
            is the only thing, until the heading.
          */}
          <BrandLogo size="card" label="Parinaam Foundation" />
          <h1 className={styles.title}>Volunteer with Parinaam</h1>
          <p className={styles.intro}>
            Tell us a little about yourself and our team will be in touch.
          </p>
        </header>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form <-> API
// ---------------------------------------------------------------------------

/**
 * Client-side validation, for the error message that arrives without a round
 * trip. It is a courtesy, not a gate: `createRegistrationRequestSchema` re-checks
 * every one of these and is what actually protects the data (§8.40).
 */
function validate(form: FormState): FieldErrors {
  const problems: FieldErrors = {};

  if (form.first_name.trim() === '') {
    problems.first_name = 'Please tell us your first name.';
  }

  // Required since 8 Sep 2026. Only on this form — staff entering a volunteer
  // they met at a session may have a first name and nothing else yet, and that
  // path validates against a different schema.
  if (form.last_name.trim() === '') {
    problems.last_name = 'Please tell us your last name.';
  }

  if (form.state === '') {
    problems.state = 'Please choose your state.';
  }
  if (form.city_choice === '') {
    problems.city_choice = 'Please choose your city.';
  } else if (form.city_choice === OTHER_CITY && form.city_other.trim() === '') {
    problems.city_other = 'Please tell us which town or city you live in.';
  }

  // Both required now, and the phone a 10-digit mobile number. These mirror
  // `createRegistrationRequestSchema`, which is the authority — this is only here
  // so the message arrives without a round trip.
  if (form.email.trim() === '') {
    problems.email = 'Please give an email address so we can reply.';
  } else if (!isEmailish(form.email)) {
    problems.email = EMAIL_MESSAGE;
  }

  if (form.phone.trim() === '') {
    problems.phone = 'Please give a phone number so we can reach you.';
  } else if (nationalTenDigits(form.phone) === null) {
    problems.phone = 'Enter a 10-digit mobile number.';
  }

  if (form.volunteer_type === VOLUNTEER_TYPE.CORPORATE && form.organization_id === '') {
    problems.organization_id = 'Choose your organisation from the list.';
  }

  if (form.volunteer_type === VOLUNTEER_TYPE.STUDENT && form.institution === '') {
    problems.institution = 'Choose your school, college or university from the list.';
  }

  // Mandatory since 13 Sep 2026 — the age band replaced date of birth.
  if (form.age_group === '') {
    problems.age_group = 'Choose the age group you fall under.';
  }

  // "Other" without the detail records nothing — the same rule the API enforces.
  if (form.occupation === OCCUPATION.OTHER && form.occupation_other.trim() === '') {
    problems.occupation_other = 'Tell us your occupation.';
  }
  if (form.institution === INSTITUTION_OTHER && form.institution_other.trim() === '') {
    problems.institution_other = 'Tell us where you study.';
  }
  if (
    form.organization_id === OTHER_ORGANIZATION_ID &&
    form.organization_other.trim() === ''
  ) {
    problems.organization_other = 'Tell us which organisation you volunteer through.';
  }
  if (
    form.volunteering_frequency === VOLUNTEERING_FREQUENCY.OTHER &&
    form.volunteering_frequency_other.trim() === ''
  ) {
    problems.volunteering_frequency_other = 'Tell us how often you would like to volunteer.';
  }

  return problems;
}

/** Form state -> request body. Blank strings become absent, not `""`. */
function toRequestBody(form: FormState): CreateRegistrationRequestBody {
  const text = (value: string) => (value.trim() === '' ? null : value.trim());

  return {
    volunteer_type: form.volunteer_type,
    // Only a corporate volunteer carries one; anyone else sends null, which is
    // what the API requires.
    organization_id:
      form.volunteer_type === VOLUNTEER_TYPE.CORPORATE ? form.organization_id || null : null,
    // The student's counterpart, and null for anybody else for the same reason:
    // the API and the CHECK constraint both refuse a value on a non-student.
    institution: form.volunteer_type === VOLUNTEER_TYPE.STUDENT ? form.institution || null : null,
    // Only OTHER carries a college, and only a student carries one at all.
    institution_other:
      form.volunteer_type === VOLUNTEER_TYPE.STUDENT && form.institution === INSTITUTION_OTHER
        ? text(form.institution_other)
        : null,
    // The same, for the employer behind the "Other — not listed" partner.
    organization_other:
      form.volunteer_type === VOLUNTEER_TYPE.CORPORATE &&
      form.organization_id === OTHER_ORGANIZATION_ID
        ? text(form.organization_other)
        : null,
    first_name: form.first_name.trim(),
    // Required by the API now, so sent as a plain trimmed string rather than
    // through `text()` — validation has already refused an empty one.
    last_name: form.last_name.trim(),
    email: text(form.email),
    phone: text(form.phone),
    // The state from the list, and the city either from the list or — when they
    // chose "Other" — the one they typed. Stored as plain names.
    state: text(form.state),
    city: form.city_choice === OTHER_CITY ? form.city_other.trim() : form.city_choice.trim(),
    age_group: text(form.age_group),
    gender: text(form.gender),
    occupation: text(form.occupation),
    // Only OTHER carries a specified detail; every coded occupation sends null,
    // so a code the visitor changed their mind about leaves nothing behind.
    occupation_other: form.occupation === OCCUPATION.OTHER ? text(form.occupation_other) : null,
    volunteering_frequency: form.volunteering_frequency || null,
    // The open frequency answer, only when OTHER is chosen.
    volunteering_frequency_other:
      form.volunteering_frequency === VOLUNTEERING_FREQUENCY.OTHER
        ? text(form.volunteering_frequency_other)
        : null,
    referral_source: text(form.referral_source),
    areas_of_interest: joinCodes(form.areas_of_interest),
    skills: text(form.skills),
  };
}

/** A 422's `details` list, keyed by field so each input can show its own message. */
function toFieldErrors(error: unknown): FieldErrors {
  if (!(error instanceof ApiRequestError) || !error.isValidation) return {};
  if (!Array.isArray(error.details)) return {};

  const problems: FieldErrors = {};
  for (const detail of error.details as FieldError[]) {
    const key = detail.field as keyof FormState;
    if (key in EMPTY_FORM) problems[key] = detail.message;
  }
  return problems;
}

/**
 * The message shown above the form: a failure that no single field explains.
 *
 * A 422 whose fields were all matched is already displayed inline, and repeating
 * it at the top would report one mistake twice.
 */
function generalMessage(error: unknown, fieldErrors: FieldErrors): string | null {
  if (!(error instanceof ApiRequestError)) return null;
  if (error.isValidation && Object.keys(fieldErrors).length > 0) return null;
  return error.message;
}
